import React, { createContext, useContext, useState, useEffect } from 'react';
import { GameAccount } from '../../authentication/Authentication.model';
import { useConfig } from '../../ConfigContext';
import { RequestError } from '../../Error.model';
import { useSnackbar } from '../../snackbar/SnackbarContext';
import { get, post } from '../../utils/Request';
import { GameIds } from '../my-games/MyGames.model';
import { Promotion, GamePromotions, PromotionStatus } from './Promotion.model';

interface PromoContext {
  rewardGameId?: string;
  requiredGameId?: string;
  promotions: Promotion[];
  groupedPromotions: GamePromotions[];
  getPromotions: () => Promise<void>;
  claim: (promoId: string, platform?: string) => Promise<void>;
  activate: (provider: string, providerId: string, activateGameId: GameIds) => Promise<void>;
}

const PromotionContext = createContext<PromoContext>({
  rewardGameId: null,
  requiredGameId: null,
  promotions: [],
  groupedPromotions: [],
  claim: null,
  getPromotions: null,
  activate: null,
});

export const usePromotion = (): PromoContext => useContext(PromotionContext);

const useProvidePromotion = (rewardGameId?: GameIds, requiredGameId?: GameIds): PromoContext => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [groupedPromotions, setGroupedPromotions] = useState<GamePromotions[]>([]);
  const config = useConfig();
  const snackbar = useSnackbar();

  const groupPromotionsByRequiredGame = (promos: Promotion[]): GamePromotions[] => {
    const requiredGames: GamePromotions[] = [];
    promos.forEach((promo) => {
      if (!promo.promotionData.requiredGameIds || promo.promotionData.requiredGameIds.length === 0) {
        return;
      }
      const currentRequiredGame = promo.promotionData.requiredGameIds[0];
      const requiredGameIndex = requiredGames.findIndex((x) => x.requiredGameId === currentRequiredGame);

      if (requiredGameIndex === -1) {
        requiredGames.push({ requiredGameId: currentRequiredGame, promotions: [promo] });
        return;
      }
      requiredGames[requiredGameIndex].promotions.push(promo);
    });

    return requiredGames;
  };

  async function getPromotions(): Promise<void> {
    if (!rewardGameId && !requiredGameId) {
      throw new Error('no GameId');
    }

    try {
      if (rewardGameId && !requiredGameId) {
        const promos: Promotion[] = await get(
          `${config.clientConfig.host}/players/me/games/${rewardGameId}/promotions`,
        );
        setPromotions(promos);
      }
      if (!rewardGameId && requiredGameId) {
        const promos: Promotion[] = await get(
          `${config.clientConfig.host}/players/me/games/${requiredGameId}/promotions?byRequiredGameId=true`,
        );
        setPromotions(promos);
      }
      if (rewardGameId && requiredGameId) {
        const promos: Promotion[] = await get(
          `${config.clientConfig.host}/players/me/games/${rewardGameId}/promotions`,
        );
        const filteredPromos = groupPromotionsByRequiredGame(promos)
          .find((x) => x.requiredGameId === requiredGameId)?.promotions;
        setPromotions(filteredPromos ?? []);
      }
    } catch (e) {
      const err = e as RequestError;
      snackbar.addErrorMessage(err.code);
    }
  }

  const claim = async (promoId: string, platformName?: string): Promise<void> => {
    try {
      const url = `${config.clientConfig.host}/players/me/promotions/${promoId}/claim`;
      const { promocode } = await post<{ promocode: string, platform?: string }>(url, platformName
        ? { data: { platformName } } : undefined);
      const promos = [...promotions];
      const promoIndex = promos.findIndex((promotion) => promotion.promotionData.promotionId === promoId);
      const promo: Promotion = {
        ...promotions[promoIndex],
        userData: {
          status: PromotionStatus.Claimed,
          promocode: { code: promocode },
        },
      };
      promos[promoIndex] = promo;
      setPromotions(promos);
    } catch (e) {
      const err = e as RequestError;
      snackbar.addErrorMessage(err.code || '404');
      throw e;
    }
  };

  const activate = async (provider: string, providerId: string, activateGameId: GameIds): Promise<void> => {
    try {
      const response = await post<{ gameAccount: GameAccount, promotions: Promotion[] }>(
        `${config.clientConfig.host}/players/me/accounts/${provider}/${providerId}/games`,
        {
          data: {
            gameId: activateGameId,
            providerSpecificData: null,
          },
        },
      );
      const promoIndex = promotions.findIndex((x) => x.promotionData.requiredGameIds.includes(activateGameId));
      const promoResponse = response.promotions.find((x) => x.promotionData.requiredGameIds.includes(activateGameId));
      if (promoIndex >= 0 && promoResponse) {
        const promos = [...promotions];
        promos[promoIndex].userData = promoResponse.userData;
        setPromotions(promos);
      }
    } catch (e) {
      const err = e as RequestError;

      if (err.code !== 'GameNotOwned') {
        snackbar.addErrorMessage(err.code);
      } else {
        throw e;
      }
    }
  };

  useEffect(() => {
    getPromotions();
  }, [rewardGameId, requiredGameId]);

  useEffect(() => {
    setGroupedPromotions(groupPromotionsByRequiredGame(promotions));
  }, [promotions]);

  return {
    rewardGameId,
    requiredGameId,
    promotions,
    groupedPromotions,
    getPromotions,
    claim,
    activate,
  };
};

export function ProvidePromotion({
  children,
  rewardGameId = null,
  requiredGameId = null,
}: {
  children: JSX.Element | React.ReactNode;
  rewardGameId?: GameIds | null;
  requiredGameId?: GameIds | null;
}): JSX.Element {
  const promotion = useProvidePromotion(rewardGameId, requiredGameId);

  return <PromotionContext.Provider value={promotion}>{children}</PromotionContext.Provider>;
}
